/**
 * ⚙️ Workflow Builder — Lista de workflows
 * Ruta: /apps/workflows
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Text,
  Button,
  Modal,
  TextField,
  Select,
  Badge,
  EmptyState,
  Divider,
  Box,
  Spinner,
  Checkbox,
} from '@/lib/polaris';
import {
  AutomationIcon,
  EditIcon,
  DeleteIcon,
  PlayIcon,
  PauseCircleIcon,
  PlusIcon,
} from '@/lib/polaris-icons';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { getErrorMessage } from '@/lib/getErrorMessage';

interface Workflow {
  id: string;
  name: string;
  description: string | null;
  status: string;
  trigger_type: string;
  enrollment_count: number;
  completed_count: number;
  error_count: number;
  last_triggered_at: string | null;
  created_at: string;
}

const TRIGGER_LABELS: Record<string, string> = {
  contact_created: 'Contacto creado',
  contact_updated: 'Contacto actualizado',
  deal_created: 'Deal creado',
  deal_stage_changed: 'Deal cambió etapa',
  deal_won: 'Deal ganado',
  deal_lost: 'Deal perdido',
  order_created: 'Pedido creado',
  order_paid: 'Pedido pagado',
  form_submitted: 'Formulario enviado',
  email_opened: 'Email abierto',
  email_clicked: 'Email clickeado',
  manual: 'Ejecución manual',
  scheduled: 'Programado (cron)',
  webhook_received: 'Webhook recibido',
};

const TRIGGER_OPTIONS = Object.entries(TRIGGER_LABELS).map(([value, label]) => ({ label, value }));

const STATUS_TONES: Record<string, 'success' | 'warning' | 'info' | 'new'> = {
  active: 'success',
  paused: 'warning',
  draft: 'info',
  archived: 'new',
};

export default function WorkflowsListPage() {
  const storeNavigate = useStoreNavigate();
  const { tenantId } = useAuth();
  const toast = useAdminToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Workflow | null>(null);

  // Form
  const [wfName, setWfName] = useState('');
  const [wfDesc, setWfDesc] = useState('');
  const [wfTrigger, setWfTrigger] = useState('contact_created');

  // Por la caché (ADR-0012, paso 3).
  const qc = useQueryClient();

  const consulta = useQuery({
    queryKey: ['workflows', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async (): Promise<Workflow[]> => {
      const { data, error } = await supabase
        .from('workflows')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Workflow[];
    },
  });

  const workflows = useMemo(() => consulta.data ?? [], [consulta.data]);
  // 🪤 `isPending`, no `isFetching`.
  const loading = !!tenantId && consulta.isPending;

  useEffect(() => {
    if (consulta.error) toast.error('Error al cargar workflows', getErrorMessage(consulta.error));
  }, [consulta.error, toast]);

  const loadWorkflows = useCallback(async () => { await consulta.refetch(); }, [consulta]);

  const handleCreate = async () => {
    if (!tenantId || !wfName.trim()) return;
    setSaving(true);

    // Create with default trigger node
    const triggerId = 'trigger-1';
    const defaultNodes = [
      {
        id: triggerId,
        type: 'trigger',
        position: { x: 250, y: 50 },
        data: { label: TRIGGER_LABELS[wfTrigger] || wfTrigger },
      },
    ];

    const row = {
      tenant_id: tenantId,
      name: wfName.trim(),
      description: wfDesc.trim() || null,
      trigger_type: wfTrigger,
      nodes: defaultNodes,
      edges: [],
    };

    try {
      const { data, error } = await supabase
        .from('workflows')
        .insert(row)
        .select('id')
        .single();
      if (error) throw error;
      toast.success('Workflow creado');
      setDialogOpen(false);
      if (data) {
        // 🪤 Crear navega al editor, así que esta lista no se vuelve a montar y nadie la recarga. Sin
        // invalidar, al volver se vería SIN lo recién creado hasta que la caché venciera sola — un
        // "no se guardó" que no ocurrió. Es el precio de cachear, y se paga avisando en la mutación.
        void qc.invalidateQueries({ queryKey: ['workflows', tenantId] });
        storeNavigate(`/apps/workflows/${data.id}`);
      }
    } catch (error: unknown) {
      toast.error('Error al crear workflow', getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (wf: Workflow) => {
    const newStatus = wf.status === 'active' ? 'paused' : 'active';
    const { error } = await supabase
      .from('workflows')
      .update({ status: newStatus })
      .eq('id', wf.id);
    if (error) {
      toast.error('Error al cambiar estado');
    } else {
      toast.success(newStatus === 'active' ? 'Workflow activado' : 'Workflow pausado');
      loadWorkflows();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const { error } = await supabase.from('workflows').delete().eq('id', deleteTarget.id);
      if (error) throw error;
      toast.success('Workflow eliminado');
      setDeleteTarget(null);
      await loadWorkflows();
    } catch (error: unknown) {
      toast.error('Error al eliminar', getErrorMessage(error));
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Nunca';
    return new Date(dateStr).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Page
      title="Workflows"
      subtitle="Automatizaciones visuales para tu negocio"
      primaryAction={{ content: 'Crear workflow', icon: PlusIcon, onAction: () => { setWfName(''); setWfDesc(''); setWfTrigger('contact_created'); setDialogOpen(true); } }}
    >
      {loading ? (
        <Card><Box padding="800"><InlineStack align="center"><Spinner size="large" /></InlineStack></Box></Card>
      ) : workflows.length === 0 ? (
        <Card>
          <EmptyState
            heading="Crea tu primer workflow"
            image=""
            action={{ content: 'Crear workflow', onAction: () => setDialogOpen(true) }}
          >
            <Text as="p">
              Los workflows automatizan tareas repetitivas: enviar emails, actualizar campos,
              crear tareas, mover deals por el pipeline — todo basado en triggers como "contacto creado"
              o "deal ganado".
            </Text>
          </EmptyState>
        </Card>
      ) : (
        <Card padding="0">
          <BlockStack gap="0">
            {workflows.map((wf, idx) => (
              <div key={wf.id}>
                <Box padding="400">
                  <InlineStack align="space-between" blockAlign="center">
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="span" fontWeight="medium">{wf.name}</Text>
                        <Badge tone={STATUS_TONES[wf.status] ?? 'new'}>
                          {wf.status === 'active' ? 'Activo' : wf.status === 'paused' ? 'Pausado' : wf.status === 'draft' ? 'Borrador' : 'Archivado'}
                        </Badge>
                        <Badge tone="info">{TRIGGER_LABELS[wf.trigger_type] ?? wf.trigger_type}</Badge>
                      </InlineStack>
                      {wf.description && <Text as="p" tone="subdued" variant="bodySm">{wf.description}</Text>}
                      <InlineStack gap="300">
                        <Text as="span" tone="subdued" variant="bodySm">
                          {wf.enrollment_count} enrollments · {wf.completed_count} completados · {wf.error_count} errores
                        </Text>
                        <Text as="span" tone="subdued" variant="bodySm">
                          Último: {formatDate(wf.last_triggered_at)}
                        </Text>
                      </InlineStack>
                    </BlockStack>
                    <InlineStack gap="100">
                      <Button
                        icon={wf.status === 'active' ? PauseCircleIcon : PlayIcon}
                        onClick={() => handleToggleStatus(wf)}
                        variant="plain"
                        size="slim"
                        accessibilityLabel={wf.status === 'active' ? 'Pausar' : 'Activar'}
                      />
                      <Button
                        icon={EditIcon}
                        onClick={() => storeNavigate(`/apps/workflows/${wf.id}`)}
                        variant="plain"
                        size="slim"
                        accessibilityLabel="Editar"
                      />
                      <Button
                        icon={DeleteIcon}
                        onClick={() => setDeleteTarget(wf)}
                        variant="plain"
                        tone="critical"
                        size="slim"
                        accessibilityLabel="Eliminar"
                      />
                    </InlineStack>
                  </InlineStack>
                </Box>
                {idx < workflows.length - 1 && <Divider />}
              </div>
            ))}
          </BlockStack>
        </Card>
      )}

      {/* Modal Crear */}
      <Modal
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Nuevo workflow"
        primaryAction={{ content: 'Crear y editar', onAction: handleCreate, loading: saving, disabled: !wfName.trim() }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setDialogOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField label="Nombre" value={wfName} onChange={setWfName} placeholder="Ej: Bienvenida a nuevos leads" autoComplete="off" requiredIndicator />
            <TextField label="Descripción" value={wfDesc} onChange={setWfDesc} placeholder="Envía email + asigna agente cuando llega un lead nuevo" autoComplete="off" multiline={2} />
            <Select label="Trigger (disparador)" options={TRIGGER_OPTIONS} value={wfTrigger} onChange={setWfTrigger} helpText="Evento que activa este workflow" />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Modal Eliminar */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar workflow"
        primaryAction={{ content: 'Eliminar', onAction: handleDelete, destructive: true }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setDeleteTarget(null) }]}
      >
        <Modal.Section>
          <Text as="p">¿Eliminar el workflow <strong>{deleteTarget?.name}</strong> y todos sus enrollments?</Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
